/**
 * Board identity for local community feeds.
 *
 * A board is uniquely defined by:
 *   - audience: who can post — 'everyone' (public) or 'artists' (artist-only desk)
 *   - place:    where it belongs — global (worldwide) or a specific city
 *
 * Concrete board ids stored on each post:
 *   global_public, global_artists, city_<slug>_public, city_<slug>_artists
 */

/** Prefix for subreddit-style board titles in the top bar (e.g. gmt/helsinkitats). */
export const COMMUNITY_BOARD_HEADER_PREFIX = 'gmt'
export const COMMUNITY_BOARD_WORLD_TITLE_SLUG = 'worldwidetats'
export const COMMUNITY_BOARD_CITY_TITLE_SUFFIX = 'tats'

export type CommunityAudience = 'everyone' | 'artists'

export type CommunityPlace =
  | { readonly kind: 'global' }
  | { readonly kind: 'city'; readonly cityLabel: string }

export function communityCitySlugFromLabel(cityLabel: string): string {
  const normalized = cityLabel
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/gi, '_')
    .replace(/^_+|_+$/g, '')
  const trimmed = normalized.length > 80 ? normalized.slice(0, 80) : normalized
  return trimmed.length > 0 ? trimmed : 'unknown'
}

export function buildCommunityBoardId(audience: CommunityAudience, place: CommunityPlace): string {
  if (place.kind === 'global') {
    return audience === 'everyone' ? 'global_public' : 'global_artists'
  }
  const slug = communityCitySlugFromLabel(place.cityLabel)
  return audience === 'everyone' ? `city_${slug}_public` : `city_${slug}_artists`
}

export function isArtistOnlyBoard(boardId: string): boolean {
  return boardId === 'global_artists' || (boardId.startsWith('city_') && boardId.endsWith('_artists'))
}

export function isGlobalBoard(boardId: string): boolean {
  return boardId === 'global_public' || boardId === 'global_artists'
}

export function cityLabelFromBoardId(boardId: string): string | null {
  const match = /^city_(.+)_(public|artists)$/u.exec(boardId)
  if (!match?.[1]?.length) return null
  const rough = match[1].replace(/_/g, ' ')
  if (!rough.length) return null
  return rough.charAt(0).toUpperCase() + rough.slice(1)
}

export function resolveCommunityPostPlaceLabel(params: {
  readonly boardId: string
  readonly cityDisplayLabel?: string | null
  readonly worldwideLabel: string
}): string {
  const storedCityLabel = params.cityDisplayLabel?.trim()
  if (storedCityLabel && storedCityLabel.length > 0) return storedCityLabel

  const cityLabelFromBoard = cityLabelFromBoardId(params.boardId)
  if (cityLabelFromBoard) return cityLabelFromBoard

  return params.worldwideLabel
}

export function isCityCommunityBoard(boardId: string): boolean {
  return cityLabelFromBoardId(boardId) != null
}

/** Map an artist-only board to its public counterpart (same place). */
export function publicBoardFromArtistBoard(boardId: string): string {
  if (boardId === 'global_artists') return 'global_public'
  if (boardId.endsWith('_artists')) return boardId.replace(/_artists$/u, '_public')
  return boardId
}

export function communityBoardCityTitleSlug(cityLabel: string): string {
  const citySlugWithoutUnderscores = communityCitySlugFromLabel(cityLabel).replace(/_/g, '')
  return `${citySlugWithoutUnderscores}${COMMUNITY_BOARD_CITY_TITLE_SUFFIX}`
}

export function communityBoardScopeTitleSlug(scopeSlug: string): string {
  const normalizedScopeSlug = scopeSlug.trim().toLowerCase()
  if (!normalizedScopeSlug.length) {
    return COMMUNITY_BOARD_WORLD_TITLE_SLUG
  }
  if (normalizedScopeSlug.endsWith(COMMUNITY_BOARD_CITY_TITLE_SUFFIX)) {
    return normalizedScopeSlug
  }
  return `${normalizedScopeSlug}${COMMUNITY_BOARD_CITY_TITLE_SUFFIX}`
}

/** Subreddit-style board title shown in the page header, e.g. gmt/helsinkitats */
export function boardSubredditTitle(boardId: string): string {
  if (isGlobalBoard(boardId)) {
    return `${COMMUNITY_BOARD_HEADER_PREFIX}/${COMMUNITY_BOARD_WORLD_TITLE_SLUG}`
  }

  const cityLabel = cityLabelFromBoardId(boardId)
  if (!cityLabel) return `${COMMUNITY_BOARD_HEADER_PREFIX}/board`

  return `${COMMUNITY_BOARD_HEADER_PREFIX}/${communityBoardCityTitleSlug(cityLabel)}`
}
